import React, { useState } from "react";
import type { TFunction } from "i18next";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime } from "@/utils/dateUtils";
import type { PartnerDestinationHotel } from "@/types/partner-destination";
import type { PartnerProviderLogsFilters } from "@/types/partner-provider-log";
import { adminPartnersService } from "@/services/admin-partners-service";
import {
  useAdminHotelSyncStatus,
  useAdminPartnerLogs,
  useTriggerAdminHotelSync,
  useUpdateAdminHotelAutoSync,
} from "../hooks/admin-hotel-partner-sync.hooks";
import { PartnerProviderLogsTable } from "@/components/pages/partner-integration-settings/components/PartnerProviderLogsTable";
import {
  buildPartnerProviderLogsExportFilename,
  downloadPartnerProviderLogsCsv,
  downloadPartnerProviderLogsExcel,
  mapPartnerProviderLogsForExport,
} from "@/components/pages/partner-integration-settings/utils/exportPartnerProviderLogs";
import type { PartnerProviderLog } from "@/types/partner-provider-log";

const syncStateBadgeVariant = (
  state: string,
): "secondary" | "success" | "destructive" | "outline" => {
  if (state === "succeeded") return "success";
  if (state === "failed") return "destructive";
  if (state === "partial") return "outline";
  if (state === "queued" || state === "running") return "outline";
  return "secondary";
};

/** Per-hotel provider log dialog — reuses the same table this codebase already had for hotel scope. */
function HotelSyncLogDialog({
  t,
  hotel,
  open,
  onOpenChange,
}: {
  t: TFunction;
  hotel: PartnerDestinationHotel;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [filters, setFilters] = useState<PartnerProviderLogsFilters>({
    page: 1,
    limit: 20,
  });
  const [isExporting, setIsExporting] = useState(false);
  const { data, isLoading } = useAdminPartnerLogs(
    open ? hotel.tenantId : undefined,
    filters,
  );

  const handleExport = async (format: "csv" | "excel") => {
    setIsExporting(true);
    try {
      const baseFilters: PartnerProviderLogsFilters = { ...filters, page: 1 };
      const firstPage = await adminPartnersService.getPartnerLogs(
        hotel.tenantId,
        baseFilters,
      );
      const allLogs: PartnerProviderLog[] = [...firstPage.data];
      const totalPages = Math.max(
        1,
        Math.ceil(firstPage.meta.total / Math.max(firstPage.meta.limit, 1)),
      );
      for (let page = 2; page <= totalPages; page += 1) {
        const next = await adminPartnersService.getPartnerLogs(hotel.tenantId, {
          ...baseFilters,
          page,
        });
        allLogs.push(...next.data);
      }

      if (allLogs.length === 0) {
        toast.error(t("partners.admin.export.noData"));
        return;
      }

      const rows = mapPartnerProviderLogsForExport(allLogs, {
        getEventLabel: (value) =>
          t(`partners.admin.eventTypeValues.${value}`, { defaultValue: value }),
        getStatusLabel: (value) =>
          t(`partners.admin.logStatusValues.${value}`, { defaultValue: value }),
        getLevelLabel: (value) =>
          t(`partners.admin.logLevelValues.${value}`, { defaultValue: value }),
      });

      if (format === "csv") {
        downloadPartnerProviderLogsCsv(
          rows,
          buildPartnerProviderLogsExportFilename("csv"),
        );
      } else {
        downloadPartnerProviderLogsExcel(
          rows,
          buildPartnerProviderLogsExportFilename("xls"),
        );
      }
    } catch {
      toast.error(t("partners.admin.export.error"));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogTitle>
          {t("partners.admin.hotelSync.logDialogTitle", {
            hotel: hotel.name,
          })}
        </DialogTitle>
        <PartnerProviderLogsTable
          isLoading={isLoading}
          isExporting={isExporting}
          logs={data?.data ?? []}
          meta={data?.meta ?? null}
          filters={filters}
          onFiltersChange={setFilters}
          onExport={handleExport}
        />
      </DialogContent>
    </Dialog>
  );
}

function HotelSyncRow({
  t,
  hotel,
  canWrite,
}: {
  t: TFunction;
  hotel: PartnerDestinationHotel;
  canWrite: boolean;
}): React.JSX.Element {
  const [isLogOpen, setIsLogOpen] = useState(false);
  const { data: status, isLoading } = useAdminHotelSyncStatus(
    hotel.hotelId,
    hotel.tenantId,
  );
  const { triggerSync, isTriggering } = useTriggerAdminHotelSync(
    hotel.hotelId,
    hotel.tenantId,
  );
  const { setAutoSync, isSavingAutoSync } = useUpdateAdminHotelAutoSync(
    hotel.hotelId,
    hotel.tenantId,
  );

  const isBusy =
    isTriggering || status?.state === "queued" || status?.state === "running";

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3">
      <div className="flex flex-col gap-1">
        <span className="text-foreground text-sm font-medium">
          {hotel.name}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          {status && (
            <>
              <Badge variant={syncStateBadgeVariant(status.state)}>
                {t(
                  `partners.admin.destinations.syncStateValues.${status.state}`,
                  {
                    defaultValue: status.state,
                  },
                )}
              </Badge>
              <span className="text-muted-foreground text-xs">
                {status.lastSyncAt
                  ? formatDateTime(status.lastSyncAt)
                  : t("partners.admin.hotelSync.neverSynced")}
              </span>
            </>
          )}
          {isLoading && !status && (
            <span className="text-muted-foreground text-xs">
              {t("partners.admin.destinations.loading")}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Switch
            checked={Boolean(status?.autoSyncEnabled)}
            disabled={!canWrite || !status || isSavingAutoSync}
            onCheckedChange={(checked) => setAutoSync(checked)}
          />
          <span className="text-muted-foreground text-xs">
            {t("partners.admin.hotelSync.columns.autoSync")}
          </span>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canWrite || isBusy}
          onClick={() => triggerSync()}
        >
          <RefreshCw className={isBusy ? "size-4 animate-spin" : "size-4"} />
          {isBusy
            ? t("partners.admin.hotelSync.syncing")
            : t("partners.admin.hotelSync.syncNow")}
        </Button>

        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setIsLogOpen(true)}
        >
          {t("partners.admin.hotelSync.viewLog")}
        </Button>
      </div>

      <HotelSyncLogDialog
        t={t}
        hotel={hotel}
        open={isLogOpen}
        onOpenChange={setIsLogOpen}
      />
    </div>
  );
}

/**
 * Task 0036 — per-hotel sync management, Guesteria-admin only (this
 * component is only ever mounted behind the Cities pages' `SuperAdminGuard`).
 * Replaces the old plain hotel badge list — sync is admin-managed now, so
 * the city's own hotel list is the natural place to trigger/inspect it.
 */
export function HotelPartnerSyncTable({
  t,
  hotels,
  canWrite,
}: {
  t: TFunction;
  hotels: PartnerDestinationHotel[];
  canWrite: boolean;
}): React.JSX.Element {
  if (hotels.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {t("partners.admin.destinations.detail.hotelsEmpty")}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {hotels.map((hotel) => (
        <HotelSyncRow
          key={hotel.hotelId}
          t={t}
          hotel={hotel}
          canWrite={canWrite}
        />
      ))}
    </div>
  );
}
